import type { Call } from '@entities/call';
import { CallTypes } from '@entities/call';
import type { BaseUsecase } from '@usecases/base-usecase';
import { RepositoryFactory } from '@main/factories/repository.factory';
import type { CallRepository } from '@infra/repositories/call.repository';
import type { UserRepository } from '@infra/repositories/user.repository';

export type ConnectedTransferCallRequest = void;

export interface ConnectedTransferCallRepository {
  fetchTransferCall(): Promise<Call>;
  fetchCallById(id: string): Promise<Call | undefined>;
}

export interface ConnectedTransferCallEventPublisher {
  publish(
    call: Call,
    isRecordingPaused?: boolean,
    recordEnabled?: boolean,
  ): Promise<void>;
}

export interface ConnectedTransferCallUsecase
  extends BaseUsecase<ConnectedTransferCallRequest, void> {
  run(
    call: ConnectedTransferCallRequest | ConnectedTransferCallWithCrmOpsRequest,
  ): Promise<void>;
}

export class ConnectedTransferCall implements ConnectedTransferCallUsecase {
  private callRepository: CallRepository;
  private userRepository: UserRepository;

  constructor(
    private repository: ConnectedTransferCallRepository,
    private publisher: ConnectedTransferCallEventPublisher,
  ) {
    this.callRepository = RepositoryFactory.buildMemCallRepository();
    this.userRepository = RepositoryFactory.buildMemUserRepository();
  }

  async run(): Promise<void> {
    const call = await this.repository.fetchTransferCall();

    if (call.type === CallTypes.ADD_PARTICIPANT && !call.isOnHold) {
      await this.publisher.publish(call);
    }
  }
}

export interface ConnectedTransferCallCrmOps {
  preCall?: (callUuid: string, req: Call) => Promise<void>;
  postCall?: (req: Call) => Promise<void>;
}

export type ConnectedTransferCallWithCrmOpsRequest = {
  callUuid: string;
  from: string;
  to: string;
};

export class ConnectedTransferCallWithCrmOps extends ConnectedTransferCall {
  constructor(
    private crmOps: ConnectedTransferCallCrmOps,
    private callRepo: ConnectedTransferCallRepository,
    publisher: ConnectedTransferCallEventPublisher,
  ) {
    super(callRepo, publisher);
  }

  override async run(
    req: ConnectedTransferCallWithCrmOpsRequest | void,
  ): Promise<void> {
    const call = await this.callRepo.fetchTransferCall();

    if (req && this.crmOps.preCall) {
      await this.crmOps.preCall(req.callUuid, call);
    }

    await super.run();

    if (req && this.crmOps.postCall) {
      await this.crmOps.postCall(call);
    }
  }
}
